import { LegalPageLayout } from "@/components/legal/LegalPageLayout";
import { LegalSection } from "@/components/legal/LegalSection";
import {
  CookieIntroduction,
  WhatAreCookies,
  TypesOfCookies,
} from "./components";

const tableOfContents = [
  { id: "introduction", title: "Introduction", level: 1 },
  { id: "what-are-cookies", title: "What Are Cookies", level: 1 },
  { id: "types-of-cookies", title: "Types of Cookies We Use", level: 1 },
  { id: "cookie-purposes", title: "How We Use Cookies", level: 1 },
  { id: "third-party-cookies", title: "Third-Party Cookies", level: 1 },
  {
    id: "cookie-management",
    title: "Managing Your Cookie Preferences",
    level: 1,
  },
  { id: "cookie-list", title: "Detailed Cookie Information", level: 1 },
  { id: "updates", title: "Updates to This Policy", level: 1 },
  { id: "contact", title: "Contact Us", level: 1 },
];

export default function CookiePolicyPage() {
  return (
    <LegalPageLayout
      title="Cookie Policy"
      lastUpdated="January 6, 2025"
      language="en"
      alternateUrl="/pages/vi/cookies"
      tableOfContents={tableOfContents}
    >
      <CookieIntroduction />
      <WhatAreCookies />
      <TypesOfCookies />

      <LegalSection id="cookie-purposes" title="How We Use Cookies">
        <h3>Authentication and Security</h3>
        <p>We use cookies to:</p>
        <ul>
          <li>Keep you logged in to your account</li>
          <li>Protect against unauthorized access</li>
          <li>Prevent cross-site request forgery (CSRF) attacks</li>
          <li>Maintain secure connections</li>
        </ul>

        <h3>User Experience Enhancement</h3>
        <p>Cookies help us:</p>
        <ul>
          <li>Remember your preferences and settings</li>
          <li>Provide personalized content and features</li>
          <li>Enable social sharing functionality</li>
          <li>Optimize page loading and performance</li>
        </ul>

        <h3>Analytics and Improvement</h3>
        <p>We analyze cookie data to:</p>
        <ul>
          <li>Understand user behavior and preferences</li>
          <li>Identify areas for website improvement</li>
          <li>Test new features and functionality</li>
          <li>Monitor website performance and reliability</li>
        </ul>

        <h3>Communication and Marketing</h3>
        <p>With your consent, we use cookies for:</p>
        <ul>
          <li>Delivering relevant advertisements</li>
          <li>Measuring marketing campaign effectiveness</li>
          <li>Personalizing marketing messages</li>
          <li>Frequency capping for ads</li>
        </ul>
      </LegalSection>

      <LegalSection id="third-party-cookies" title="Third-Party Cookies">
        <p>
          We work with various third-party service providers who may set their
          own cookies when you use our services. These include:
        </p>

        <h3>Analytics Providers</h3>
        <ul>
          <li>
            <strong>Google Analytics:</strong> Website traffic and user behavior
            analysis
          </li>
          <li>
            <strong>Facebook Analytics:</strong> Social media engagement
            tracking
          </li>
          <li>
            <strong>Mixpanel:</strong> Product analytics and user journey
            tracking
          </li>
        </ul>

        <h3>Advertising Partners</h3>
        <ul>
          <li>
            <strong>Google Ads:</strong> Targeted advertising and remarketing
          </li>
          <li>
            <strong>Facebook Ads:</strong> Social media advertising
          </li>
          <li>
            <strong>Twitter Ads:</strong> Social media marketing
          </li>
        </ul>

        <h3>Social Media Platforms</h3>
        <ul>
          <li>
            <strong>Facebook:</strong> Social login and sharing features
          </li>
          <li>
            <strong>Google:</strong> Authentication and integration services
          </li>
          <li>
            <strong>Twitter:</strong> Social sharing and embeds
          </li>
        </ul>

        <h3>Support and Communication</h3>
        <ul>
          <li>
            <strong>Intercom:</strong> Customer support chat
          </li>
          <li>
            <strong>Zendesk:</strong> Help desk and ticketing
          </li>
          <li>
            <strong>Mailchimp:</strong> Email marketing and communication
          </li>
        </ul>

        <p>
          Please note that we do not control these third-party cookies. We
          recommend reviewing the privacy policies of these providers for more
          information about their cookie practices.
        </p>
      </LegalSection>

      <LegalSection
        id="cookie-management"
        title="Managing Your Cookie Preferences"
      >
        <h3>Browser Settings</h3>
        <p>
          Most web browsers allow you to control cookies through their settings.
          You can typically:
        </p>
        <ul>
          <li>View cookies stored on your device</li>
          <li>Delete existing cookies</li>
          <li>Block future cookies</li>
          <li>Set preferences for specific websites</li>
        </ul>

        <h3>Browser-Specific Instructions</h3>
        <div className="bg-gray-50 p-4 rounded-lg">
          <h4 className="font-semibold mb-2">
            Popular Browser Cookie Settings:
          </h4>
          <ul className="space-y-1">
            <li>
              <strong>Chrome:</strong> Settings → Privacy and Security → Cookies
              and other site data
            </li>
            <li>
              <strong>Firefox:</strong> Settings → Privacy & Security → Cookies
              and Site Data
            </li>
            <li>
              <strong>Safari:</strong> Preferences → Privacy → Manage Website
              Data
            </li>
            <li>
              <strong>Edge:</strong> Settings → Site permissions → Cookies and
              site data
            </li>
          </ul>
        </div>

        <h3>Cookie Consent Management</h3>
        <p>
          When you first visit our website, we'll show you a cookie consent
          banner where you can choose which types of cookies to accept. You can
          change these preferences at any time by:
        </p>
        <ul>
          <li>Clicking the "Cookie Settings" link in our website footer</li>
          <li>Accessing your account privacy settings</li>
          <li>Using the cookie preference center</li>
        </ul>

        <h3>Opt-Out Options</h3>
        <p>For specific types of tracking, you can opt out through:</p>
        <ul>
          <li>
            <strong>Google Analytics:</strong>{" "}
            <a
              href="https://tools.google.com/dlpage/gaoptout"
              className="text-blue-600 hover:underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              Google Analytics Opt-out Browser Add-on
            </a>
          </li>
          <li>
            <strong>Advertising:</strong>{" "}
            <a
              href="http://www.aboutads.info/choices/"
              className="text-blue-600 hover:underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              Digital Advertising Alliance Opt-out
            </a>
          </li>
          <li>
            <strong>Network Advertising:</strong>{" "}
            <a
              href="http://www.networkadvertising.org/choices/"
              className="text-blue-600 hover:underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              Network Advertising Initiative Opt-out
            </a>
          </li>
        </ul>

        <h3>Impact of Disabling Cookies</h3>
        <p>
          Please note that disabling certain cookies may affect the
          functionality of our website. Essential cookies cannot be disabled as
          they are necessary for basic website operation.
        </p>
      </LegalSection>

      <LegalSection id="cookie-list" title="Detailed Cookie Information">
        <div className="overflow-x-auto">
          <table className="min-w-full border border-gray-300">
            <thead className="bg-gray-50">
              <tr>
                <th className="border border-gray-300 px-4 py-2 text-left">
                  Cookie Name
                </th>
                <th className="border border-gray-300 px-4 py-2 text-left">
                  Purpose
                </th>
                <th className="border border-gray-300 px-4 py-2 text-left">
                  Type
                </th>
                <th className="border border-gray-300 px-4 py-2 text-left">
                  Duration
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="border border-gray-300 px-4 py-2">
                  locuno_session
                </td>
                <td className="border border-gray-300 px-4 py-2">
                  User authentication and session management
                </td>
                <td className="border border-gray-300 px-4 py-2">Essential</td>
                <td className="border border-gray-300 px-4 py-2">Session</td>
              </tr>
              <tr className="bg-gray-50">
                <td className="border border-gray-300 px-4 py-2">csrf_token</td>
                <td className="border border-gray-300 px-4 py-2">
                  Cross-site request forgery protection
                </td>
                <td className="border border-gray-300 px-4 py-2">Essential</td>
                <td className="border border-gray-300 px-4 py-2">Session</td>
              </tr>
              <tr>
                <td className="border border-gray-300 px-4 py-2">
                  user_preferences
                </td>
                <td className="border border-gray-300 px-4 py-2">
                  Store user language and display preferences
                </td>
                <td className="border border-gray-300 px-4 py-2">Functional</td>
                <td className="border border-gray-300 px-4 py-2">1 year</td>
              </tr>
              <tr className="bg-gray-50">
                <td className="border border-gray-300 px-4 py-2">_ga</td>
                <td className="border border-gray-300 px-4 py-2">
                  Google Analytics - distinguish users
                </td>
                <td className="border border-gray-300 px-4 py-2">Analytics</td>
                <td className="border border-gray-300 px-4 py-2">2 years</td>
              </tr>
              <tr>
                <td className="border border-gray-300 px-4 py-2">_fbp</td>
                <td className="border border-gray-300 px-4 py-2">
                  Facebook Pixel - track conversions
                </td>
                <td className="border border-gray-300 px-4 py-2">Marketing</td>
                <td className="border border-gray-300 px-4 py-2">3 months</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className="mt-4 text-sm text-gray-600">
          This table shows the main cookies we use. Additional cookies may be
          set by third-party services integrated into our platform.
        </p>
      </LegalSection>

      <LegalSection id="updates" title="Updates to This Policy">
        <p>
          We may update this Cookie Policy from time to time to reflect changes
          in our practices, technology, or legal requirements. When we make
          significant changes, we will notify you through:
        </p>
        <ul>
          <li>An updated "Last Modified" date at the top of this policy</li>
          <li>A notification banner on our website</li>
          <li>Email notification to registered users (for material changes)</li>
          <li>In-app notifications</li>
        </ul>
        <p>
          We encourage you to review this Cookie Policy periodically to stay
          informed about how we use cookies.
        </p>
      </LegalSection>

      <LegalSection id="contact" title="Contact Us">
        <p>
          If you have any questions about our use of cookies or this Cookie
          Policy, please contact us:
        </p>
        <div className="bg-gray-50 p-4 rounded-lg mt-4">
          <p>
            <strong>Email:</strong> privacy@locuno.com
          </p>
          <p>
            <strong>Subject Line:</strong> Cookie Policy Inquiry
          </p>
          <p>
            <strong>Address:</strong> Cloudjet Solutions Pte. Ltd.
            <br />
            71 Ayer Rajah Crescent
            <br />
            #02-10/11
            <br />
            Singapore 139951
          </p>
          <p>
            <strong>Data Protection Officer:</strong> dpo@locuno.com
          </p>
        </div>
        <p className="mt-4">
          For technical issues related to cookie settings or website
          functionality, please contact our support team at{" "}
          <a
            href="mailto:support@locuno.com"
            className="text-blue-600 hover:underline"
          >
            support@locuno.com
          </a>
          .
        </p>
      </LegalSection>
    </LegalPageLayout>
  );
}
